import { ALIGNMENT_STATUS, COMMUNICATION_SOURCES } from './communicationsTypes.js';

const SOURCE_ORDER = [
  COMMUNICATION_SOURCES.OUTLOOK,
  COMMUNICATION_SOURCES.TEAMS,
  COMMUNICATION_SOURCES.PLANNER,
];

function normalizePosition(value) {
  return String(value || '').trim().toLowerCase();
}

export function normalizeFeedItem(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    source: raw.source,
    threadKey: raw.threadKey,
    threadTopic: raw.threadTopic,
    timestamp: raw.timestamp,
    author: raw.author,
    subject: raw.subject,
    preview: raw.preview,
    body: raw.body,
    status: raw.status || '',
    linkedRefs: raw.linkedRefs || [],
    attachments: raw.attachments || [],
    metadata: raw.metadata || {},
    decisionPosition: raw.decisionPosition || '',
  };
}

export function mapCommunicationLinkToConnection(link) {
  if (!link?.type) return null;
  const base = {
    connected: true,
    name: link.name || '',
    url: link.url || '',
    externalId: link.id,
  };
  if (link.type === 'outlook_folder') return { outlook: base };
  if (link.type === 'teams_group') return { teams: { ...base, channel: link.metadata?.channel } };
  if (link.type === 'planner_board') return { planner: base };
  return null;
}

export function highlightSearchTerms(text, query) {
  const source = String(text || '');
  const q = String(query || '').trim();
  if (!q) return source;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(${escaped})`, 'gi');
  return source.replace(re, '<mark>$1</mark>');
}

export function filterFeedItems(items, filters = {}, allItems = items) {
  const { assignee, dateRange, query, threadKey, alignment } = filters;
  const activeSources = getActiveSourceFilters(filters);
  const q = String(query || '').trim().toLowerCase();
  const now = Date.now();

  return (items || []).filter((item) => {
    if (threadKey && item.threadKey !== threadKey) return false;
    if (activeSources.length && !activeSources.includes(item.source)) return false;
    if (assignee && !String(item.author || '').toLowerCase().includes(String(assignee).toLowerCase())) {
      return false;
    }
    if (dateRange === '7d') {
      const ms = new Date(item.timestamp).getTime();
      if (!Number.isNaN(ms) && now - ms > 7 * 24 * 60 * 60 * 1000) return false;
    }
    if (dateRange === '30d') {
      const ms = new Date(item.timestamp).getTime();
      if (!Number.isNaN(ms) && now - ms > 30 * 24 * 60 * 60 * 1000) return false;
    }
    if (q) {
      const haystack = [
        item.subject,
        item.preview,
        item.body,
        item.author,
        item.threadTopic,
        item.decisionPosition,
        ...(item.linkedRefs || []),
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (alignment) {
      const conflict = detectAlignmentForThread(allItems || items, item.threadKey);
      if (alignment === 'conflict') {
        if (
          conflict?.status !== ALIGNMENT_STATUS.CONFLICT &&
          conflict?.status !== ALIGNMENT_STATUS.PARTIAL
        ) {
          return false;
        }
      }
      if (alignment === 'aligned' && conflict?.status !== ALIGNMENT_STATUS.ALIGNED) {
        return false;
      }
    }
    return true;
  });
}

export function getItemsForThread(items, threadKey) {
  return (items || []).filter((i) => i.threadKey === threadKey);
}

export function getRelatedItems(items, itemId) {
  const item = (items || []).find((i) => i.id === itemId);
  if (!item) return [];
  return getItemsForThread(items, item.threadKey);
}

export function getThreadPreviewBySource(items, threadKey) {
  const threadItems = getItemsForThread(items, threadKey);
  const result = {};
  SOURCE_ORDER.forEach((src) => {
    const sorted = threadItems
      .filter((i) => i.source === src)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    result[src] = sorted[0] || null;
  });
  return result;
}

export function detectAlignmentForThread(items, threadKey) {
  const threadItems = getItemsForThread(items, threadKey);
  if (!threadItems.length) return null;

  const threadTopic = threadItems[0].threadTopic;
  const bySource = {};
  const positions = new Set();

  SOURCE_ORDER.forEach((src) => {
    const sorted = threadItems
      .filter((i) => i.source === src)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    const latest = sorted[0];
    if (latest) {
      bySource[src] = {
        excerpt: latest.preview || latest.subject,
        author: latest.author,
        timestamp: latest.timestamp,
        decisionPosition: latest.decisionPosition,
      };
      const pos = normalizePosition(latest.decisionPosition);
      if (pos && !pos.includes('pending') && !pos.includes('confirm')) {
        positions.add(pos);
      }
    }
  });

  const sourceCount = Object.keys(bySource).length;
  let status = ALIGNMENT_STATUS.MISSING;

  if (sourceCount === 0) {
    status = ALIGNMENT_STATUS.MISSING;
  } else if (sourceCount === 1) {
    status = ALIGNMENT_STATUS.PARTIAL;
  } else if (positions.size <= 1) {
    status = ALIGNMENT_STATUS.ALIGNED;
  } else {
    status = ALIGNMENT_STATUS.CONFLICT;
  }

  const plannerPos = normalizePosition(bySource.planner?.decisionPosition);
  if (plannerPos.includes('pending') && positions.size > 1) {
    status = ALIGNMENT_STATUS.CONFLICT;
  } else if (plannerPos.includes('pending') && positions.size === 1) {
    status = ALIGNMENT_STATUS.PARTIAL;
  }

  return { threadKey, threadTopic, status, bySource };
}

export function findBestThreadKeyForQuery(items, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return null;

  const threadScores = new Map();
  (items || []).forEach((item) => {
    const haystack = [
      item.subject,
      item.preview,
      item.threadTopic,
      item.decisionPosition,
    ]
      .join(' ')
      .toLowerCase();
    if (haystack.includes(q)) {
      threadScores.set(item.threadKey, (threadScores.get(item.threadKey) || 0) + 1);
    }
  });

  let best = null;
  let bestScore = 0;
  threadScores.forEach((score, key) => {
    if (score > bestScore) {
      bestScore = score;
      best = key;
    }
  });
  return best;
}

export function formatRelativeTime(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function formatCurrency(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function groupItemsBySource(items) {
  const groups = { outlook: [], teams: [], planner: [] };
  (items || []).forEach((item) => {
    if (groups[item.source]) groups[item.source].push(item);
  });
  Object.keys(groups).forEach((key) => {
    groups[key].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  });
  return groups;
}

/** @returns {import('./communicationsTypes.js').CommunicationSource[]} */
export function getActiveSourceFilters(filters = {}) {
  if (Array.isArray(filters.sources)) {
    return filters.sources.filter((s) => SOURCE_ORDER.includes(s));
  }
  if (filters.source) return [filters.source];
  return [];
}

/** @returns {import('./communicationsTypes.js').CommunicationSource[]} */
export function getVisibleSources(filters = {}) {
  const active = getActiveSourceFilters(filters);
  return active.length ? active : [...SOURCE_ORDER];
}

export function toggleSourceFilter(currentSources, source) {
  const list = Array.isArray(currentSources) ? [...currentSources] : [];
  const idx = list.indexOf(source);
  if (idx >= 0) {
    list.splice(idx, 1);
    return list;
  }
  return [...list, source];
}

function initials(name) {
  return String(name || '?')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function authorInitials(name) {
  return initials(name);
}
